// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL, { API_ENDPOINTS } from '../../config/api';
import { renderLatex, renderDiagramTag } from './latexRenderer';
import { clearAuthSession } from '../../utils/auth';
const FREE_PROMPT_LIMIT = 2;
const FREE_PROMPT_KEY = 'lw_free_prompts_used';

const SUGGESTIONS = [
    'Explain this topic from the basics',
    'Give me a practice problem',
    'What are the key formulas?',
    'How do I solve this quickly?',
];

const MODEL_OPTIONS = [
    { key: 'flash', label: 'Gemini 2.5 Flash', desc: 'Fast responses' },
    { key: 'pro',   label: 'Gemini 2.5 Pro',   desc: 'Deeper reasoning' },
];

const MODE_OPTIONS = [
    { key: 'default', label: 'Balanced' },
    { key: 'cited',   label: 'Factual' },
    { key: 'prose',   label: 'Detailed' },
];

// Parse inline bold/italic/LaTeX within a line
function renderInline(line) {
    var parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map(function(part, i) {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{renderLatex(part.slice(2, -2), i)}</strong>;
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{renderLatex(part.slice(1, -1), i)}</em>;
        return renderLatex(part, i);
    });
}

// Full markdown-lite renderer with LaTeX and diagram support
function renderAIText(text) {
    var lines = text.split('\n');
    var elements = [];
    var key = 0;

    // Collect consecutive $$ lines for multi-line display math
    var i = 0;
    while (i < lines.length) {
        var line = lines[i];
        var trimmed = line.trim();

        // Block math: standalone $$ ... $$ (may span multiple lines)
        if (trimmed.startsWith('$$') && !trimmed.endsWith('$$')) {
            // Multi-line block math — collect until closing $$
            var mathLines = [trimmed.slice(2)];
            i++;
            while (i < lines.length) {
                var nextTrimmed = lines[i].trim();
                if (nextTrimmed.endsWith('$$')) {
                    mathLines.push(nextTrimmed.slice(0, -2));
                    break;
                }
                mathLines.push(lines[i]);
                i++;
            }
            var blockLatex = mathLines.join('\n').trim();
            elements.push(<div key={key++} className="lw-md-p">{renderLatex('$$' + blockLatex + '$$', key)}</div>);
            i++;
            continue;
        }

        // Single-line block math: $$...$$
        if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length > 4) {
            elements.push(<div key={key++} className="lw-md-p">{renderLatex(trimmed, key)}</div>);
            i++;
            continue;
        }

        // Diagram tag
        if (trimmed.match(/^\[Diagram:/i)) {
            var diagramEl = renderDiagramTag(trimmed, key++);
            if (diagramEl) { elements.push(diagramEl); i++; continue; }
        }

        if (line.startsWith('#### ')) {
            elements.push(<div key={key++} className="lw-md-h4">{renderInline(line.slice(5))}</div>);
        } else if (line.startsWith('### ')) {
            elements.push(<div key={key++} className="lw-md-h3">{renderInline(line.slice(4))}</div>);
        } else if (line.startsWith('## ')) {
            elements.push(<div key={key++} className="lw-md-h2">{renderInline(line.slice(3))}</div>);
        } else if (line.startsWith('# ')) {
            elements.push(<div key={key++} className="lw-md-h2">{renderInline(line.slice(2))}</div>);
        } else if (line.startsWith('* ') || line.startsWith('- ')) {
            elements.push(
                <div key={key++} className="lw-md-li">
                    <span className="lw-md-bullet">•</span>
                    <span>{renderInline(line.slice(2))}</span>
                </div>
            );
        } else if (/^\d+\.\s/.test(line)) {
            var match = line.match(/^(\d+)\.\s(.*)/);
            elements.push(
                <div key={key++} className="lw-md-li">
                    <span className="lw-md-num">{match[1]}.</span>
                    <span>{renderInline(match[2])}</span>
                </div>
            );
        } else if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
            elements.push(<hr key={key++} className="lw-md-hr" />);
        } else if (trimmed === '') {
            elements.push(<div key={key++} className="lw-md-gap" />);
        } else {
            elements.push(<div key={key++} className="lw-md-p">{renderInline(line)}</div>);
        }
        i++;
    }

    return elements;
}

var TABS = [
    { key: 'chat',      label: 'Chat',       icon: '💬' },
    { key: 'flashcard', label: 'Flashcards',  icon: '🃏' },
    { key: 'quiz',      label: 'Quiz',        icon: '🧠' },
    { key: 'progress',  label: 'Progress',    icon: '📊' },
];

function ChatView({ topic, token, topics, selectedTopic, onTopicChange, loadingTopics, prefillText, onPrefillConsumed, activeTab, onTabChange }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPaywall, setShowPaywall] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState('');

    // Model & mode selection
    const [selectedModel, setSelectedModel] = useState('flash');
    const [selectedMode, setSelectedMode] = useState('default');
    const [showModelMenu, setShowModelMenu] = useState(false);

    // Attachment state
    const [attachedFile, setAttachedFile] = useState(null);
    const [showAttachMenu, setShowAttachMenu] = useState(false);

    // Document panel state — multiple uploads supported
    const [docSessions, setDocSessions] = useState([]); // [{ id, fileName, status, pages, totalPages, currentPage, error, loading }]
    const [activeDocIdx, setActiveDocIdx] = useState(0);
    const [showDocPanel, setShowDocPanel] = useState(false);
    const [docSelections, setDocSelections] = useState([]); // [{ id, text, docName, pageNum }]
    const [mobileDocHeight, setMobileDocHeight] = useState(null);

    // Hint state
    const [showHintInput, setShowHintInput] = useState(false);
    const [hintText, setHintText] = useState('');
    const [hintLoading, setHintLoading] = useState(false);
    const [hintResponse, setHintResponse] = useState('');

    // Subscription & free prompt tracking
    const [isSubscribed, setIsSubscribed] = useState(true); // default true to avoid flash
    const [freePromptsUsed, setFreePromptsUsed] = useState(function() {
        var stored = localStorage.getItem(FREE_PROMPT_KEY);
        return stored ? parseInt(stored, 10) || 0 : 0;
    });

    // Hover-select state
    const [hoverText, setHoverText] = useState('');
    const [hoverContext, setHoverContext] = useState(''); // surrounding sentence/paragraph
    const [hoverPos, setHoverPos] = useState({ top: 0, left: 0 });
    const [hoverQuestion, setHoverQuestion] = useState('');
    const [hoverAnswer, setHoverAnswer] = useState('');
    const [hoverLoading, setHoverLoading] = useState(false);
    const [showHoverPopup, setShowHoverPopup] = useState(false);

    const messagesContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const hoverInputRef = useRef(null);
    const startHandleRef = useRef(null);
    const endHandleRef = useRef(null);
    const askBtnRef = useRef(null);
    const abortControllerRef = useRef(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const attachMenuRef = useRef(null);

    // Apps menu state
    const [showAppsMenu, setShowAppsMenu] = useState(false);
    const appsMenuRef = useRef(null);
    const modelMenuRef = useRef(null);

    // Close apps menu on click outside
    useEffect(function() {
        if (!showAppsMenu) return;
        var handler = function(e) {
            if (appsMenuRef.current && !appsMenuRef.current.contains(e.target)) {
                setShowAppsMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return function() { document.removeEventListener('mousedown', handler); };
    }, [showAppsMenu]);

    useEffect(() => {
        setMessages([]);
        setSessionId(null);
        setInput('');
        setError('');
        setShowHoverPopup(false);
        setAttachedFile(null);
        setShowAttachMenu(false);
        setShowHintInput(false);
        setHintResponse('');
        setDocSessions([]);
        setActiveDocIdx(0);
        setShowDocPanel(false);
        setDocSelections([]);
    }, [topic]);

    // Check subscription status on mount
    useEffect(function() {
        if (!token) return;
        var headers = { 'Authorization': 'Bearer ' + token };
        fetch(API_ENDPOINTS.SUBSCRIPTION.STATUS, { headers: headers })
            .then(function(r) { return r.text(); })
            .then(function(text) {
                var trimmed = text ? text.trim() : '';
                if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                    setIsSubscribed(false);
                    return;
                }
                try {
                    var data = JSON.parse(trimmed);
                    setIsSubscribed(!!(data && data.active));
                } catch (e) {
                    setIsSubscribed(false);
                }
            })
            .catch(function() { setIsSubscribed(false); });
    }, [token]);

    // Scroll only the messages container
    useEffect(() => {
        var el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, loading]);

    // Consume prefill text from curriculum panel
    useEffect(() => {
        if (prefillText && prefillText.trim()) {
            setInput(prefillText.trim());
            if (textareaRef.current) textareaRef.current.focus();
            if (onPrefillConsumed) onPrefillConsumed();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillText]);

    // Auto-focus hover input when popup opens
    useEffect(() => {
        if (showHoverPopup && !hoverAnswer && hoverInputRef.current) {
            hoverInputRef.current.focus();
        }
    }, [showHoverPopup, hoverAnswer]);

    useEffect(() => {
        const handler = function(e) {
            if (e.key === 'Escape') { setShowHoverPopup(false); setShowAttachMenu(false); setShowModelMenu(false); }
        };
        document.addEventListener('keydown', handler);
        return function() { document.removeEventListener('keydown', handler); };
    }, []);

    // Close attach menu when clicking outside
    useEffect(() => {
        if (!showAttachMenu) return;
        const handler = function(e) {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
                setShowAttachMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return function() { document.removeEventListener('mousedown', handler); };
    }, [showAttachMenu]);

    // Close model menu when clicking outside
    useEffect(() => {
        if (!showModelMenu) return;
        const handler = function(e) {
            if (modelMenuRef.current && !modelMenuRef.current.contains(e.target)) {
                setShowModelMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return function() { document.removeEventListener('mousedown', handler); };
    }, [showModelMenu]);

    var handleFileSelect = function(e) {
        var file = e.target.files && e.target.files[0];
        if (file) {
            setAttachedFile(file);
            extractDocument(file);
        }
        e.target.value = '';
    };

    // Upload file for text extraction, append to docSessions, then poll
    var extractDocument = function(file) {
        var newIdx = docSessions.length;
        var placeholder = {
            id: null, fileName: file.name, status: 'processing',
            pages: [], totalPages: 0, currentPage: 1, error: '', loading: true,
        };
        setDocSessions(function(prev) { return prev.concat([placeholder]); });
        setActiveDocIdx(newIdx);
        setShowDocPanel(true);

        var formData = new FormData();
        formData.append('file', file);
        formData.append('topic', topic);

        fetch(API_BASE_URL + '/learnwithai/ai/document', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + token },
            body: formData,
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
            if (!data.success) {
                setDocSessions(function(prev) {
                    var copy = prev.slice();
                    copy[newIdx] = Object.assign({}, copy[newIdx], { loading: false, error: data.message || 'Upload failed', status: 'error' });
                    return copy;
                });
                return;
            }
            pollDocSession(data.data.docSessionId, newIdx);
        })
        .catch(function() {
            setDocSessions(function(prev) {
                var copy = prev.slice();
                copy[newIdx] = Object.assign({}, copy[newIdx], { loading: false, error: 'Failed to upload file', status: 'error' });
                return copy;
            });
        });
    };

    var pollDocSession = function(docId, idx) {
        var attempts = 0;
        var maxAttempts = 60;
        var poll = function() {
            fetch(API_BASE_URL + '/learnwithai/ai/document/' + docId, {
                headers: { 'Authorization': 'Bearer ' + token },
            })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (!data.success) {
                    setDocSessions(function(prev) {
                        var copy = prev.slice();
                        copy[idx] = Object.assign({}, copy[idx], { loading: false, error: 'Failed to fetch document', status: 'error' });
                        return copy;
                    });
                    return;
                }
                var doc = data.data;
                if (doc.status === 'ready') {
                    setDocSessions(function(prev) {
                        var copy = prev.slice();
                        copy[idx] = {
                            id: doc.docSessionId,
                            fileName: doc.fileName,
                            status: 'ready',
                            pages: doc.pages || [],
                            totalPages: (doc.pages && doc.pages.length) || doc.totalPages || 1,
                            currentPage: 1,
                            error: '',
                            loading: false,
                        };
                        return copy;
                    });
                } else if (doc.status === 'error') {
                    setDocSessions(function(prev) {
                        var copy = prev.slice();
                        copy[idx] = Object.assign({}, copy[idx], { loading: false, error: doc.errorMessage || 'Processing failed', status: 'error' });
                        return copy;
                    });
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setDocSessions(function(prev) {
                            var copy = prev.slice();
                            var currentProgress = copy[idx].progress || 0;
                            var maxSimulated = Math.min(99, Math.floor((attempts / maxAttempts) * 95));
                            copy[idx] = Object.assign({}, copy[idx], { progress: Math.max(currentProgress, maxSimulated) });
                            return copy;
                        });
                        setTimeout(poll, 2000);
                    } else {
                        setDocSessions(function(prev) {
                            var copy = prev.slice();
                            copy[idx] = Object.assign({}, copy[idx], { loading: false, error: 'Processing timed out', status: 'error' });
                            return copy;
                        });
                    }
                }
            })
            .catch(function() {
                setDocSessions(function(prev) {
                    var copy = prev.slice();
                    copy[idx] = Object.assign({}, copy[idx], { loading: false, error: 'Network error while checking document', status: 'error' });
                    return copy;
                });
            });
        };
        poll();
    };

    var removeDoc = function(idx) {
        setDocSessions(function(prev) {
            var copy = prev.slice();
            copy.splice(idx, 1);
            return copy;
        });
        // Remove selections that belong to this doc
        var removedName = docSessions[idx] && docSessions[idx].fileName;
        if (removedName) {
            setDocSelections(function(prev) { return prev.filter(function(s) { return s.docName !== removedName; }); });
        }
        setActiveDocIdx(function(prev) {
            if (prev >= idx && prev > 0) return prev - 1;
            return prev;
        });
        if (docSessions.length <= 1) {
            setAttachedFile(null);
            setShowDocPanel(false);
        }
    };

    var closeDocPanel = function() {
        setShowDocPanel(false);
    };

    var setDocPage = function(page) {
        setDocSessions(function(prev) {
            var copy = prev.slice();
            copy[activeDocIdx] = Object.assign({}, copy[activeDocIdx], { currentPage: page });
            return copy;
        });
    };

    var handleDocTextSelect = function() {
        var sel = window.getSelection();
        if (!sel || !sel.toString().trim()) return;
        var text = sel.toString().trim();
        var activeDoc = docSessions[activeDocIdx];
        if (!activeDoc) return;
        // Dedup check
        var exists = docSelections.some(function(s) { return s.text === text && s.docName === activeDoc.fileName && s.pageNum === activeDoc.currentPage; });
        if (exists) return;
        var newSelection = {
            id: Date.now() + '_' + Math.random().toString(36).slice(2, 7),
            text: text,
            docName: activeDoc.fileName,
            pageNum: activeDoc.currentPage,
        };
        setDocSelections(function(prev) { return prev.concat([newSelection]); });
    };

    var handleMobileResizeStart = function(e) {
        var startY = e.touches ? e.touches[0].clientY : e.clientY;
        var startHeight = mobileDocHeight || (window.innerHeight * 0.4);
        
        var onMove = function(moveEvent) {
            var currentY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
            var delta = currentY - startY;
            var newHeight = Math.max(100, Math.min(window.innerHeight - 150, startHeight + delta));
            setMobileDocHeight(newHeight);
        };
        
        var onEnd = function() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onEnd);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onEnd);
        };
        
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove);
        document.addEventListener('touchend', onEnd);
    };

    // eslint-disable-next-line no-unused-vars
    var removeDocSelection = function(selId) {
        setDocSelections(function(prev) { return prev.filter(function(s) { return s.id !== selId; }); });
    };

    // Auto-resize textarea
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = ta.scrollHeight + 'px';
    }, [input]);

    function safeFetch(url, options) {
        return fetch(url, options)
            .then(function(r) {
                if (r.status === 401 || r.status === 403) {
                    clearAuthSession();
                    window.location.href = '/signin';
                    return { success: false, message: 'Session expired' };
                }
                return r.text().then(function(text) {
                    var trimmed = text ? text.trim() : '';
                    if (!trimmed || trimmed === 'undefined') return { success: false, message: 'Empty response' };
                    return JSON.parse(trimmed);
                });
            });
    }

    var stopGeneration = function() {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setLoading(false);
        setIsStreaming(false);
    };

    var sendMessage = function(question) {
        var q = (question || input).trim();
        if (!q || isStreaming) return;

        // Enforce free prompt limit for non-subscribers
        if (!isSubscribed && freePromptsUsed >= FREE_PROMPT_LIMIT) {
            setShowPaywall(true);
            return;
        }

        if (abortControllerRef.current) abortControllerRef.current.abort();
        var controller = new AbortController();
        abortControllerRef.current = controller;

        var currentSelections = docSelections.slice();
        setInput('');
        setError('');

        var selSummary = currentSelections.length > 0
            ? '[' + currentSelections.length + ' selection' + (currentSelections.length > 1 ? 's' : '') + '] '
            : '';
        var userBubble = selSummary + q;
        setMessages(function(prev) { return prev.concat([{ from: 'user', text: userBubble }]); });

        // Track free prompt usage for non-subscribers
        if (!isSubscribed) {
            var newCount = freePromptsUsed + 1;
            setFreePromptsUsed(newCount);
            localStorage.setItem(FREE_PROMPT_KEY, String(newCount));
        }

        setLoading(true);
        setIsStreaming(true);
        setShowHoverPopup(false);
        setShowHintInput(false);
        setHintResponse('');

        var readyDocs = docSessions.filter(function(d) { return d.status === 'ready' && d.id; });
        var body = {
            topic: topic,
            userQuestion: q,
            sessionId: sessionId,
            mode: selectedMode,
            modelKey: selectedModel,
        };
        if (readyDocs.length > 0) {
            body.docSessionIds = readyDocs.map(function(d) { return d.id; });
            if (currentSelections.length > 0) {
                body.selectedDocText = currentSelections.map(function(s) { return s.text; }).join('\n---\n');
            }
        }

        var fetchUrl = API_BASE_URL + '/learnwithai/ai/ask';
        var fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        };

        fetch(fetchUrl, fetchOptions).then(function(response) {
            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                window.location.href = '/signin';
                return;
            }

            var reader = response.body.getReader();
            var decoder = new TextDecoder();
            var buffer = '';
            var gotFirstChunk = false;

            function processLine(line) {
                if (!line.startsWith('data: ')) return;
                try {
                    var data = JSON.parse(line.slice(6));
                    if (data.chunk) {
                        if (!gotFirstChunk) {
                            gotFirstChunk = true;
                            setLoading(false);
                            setMessages(function(prev) { return prev.concat([{ from: 'ai', text: data.chunk }]); });
                        } else {
                            setMessages(function(prev) {
                                var msgs = prev.slice();
                                var last = msgs[msgs.length - 1];
                                if (last && last.from === 'ai') {
                                    msgs[msgs.length - 1] = { from: 'ai', text: last.text + data.chunk };
                                }
                                return msgs;
                            });
                        }
                    }
                    if (data.done) { setSessionId(data.sessionId); setLoading(false); setIsStreaming(false); abortControllerRef.current = null; }
                    if (data.error) {
                    if (data.error.includes("Free limit reached")) {
                        setShowPaywall(true);
                    }
                    if (data.code === 'NON_ACADEMIC_CONTENT') {
                        setMessages(function(prev) { return prev.concat([{ from: 'ai', text: data.error, isAcademicNotice: true }]); });
                        setLoading(false);
                        setIsStreaming(false);
                        abortControllerRef.current = null;
                        return;
                    }
                    setError(data.error);
                    setLoading(false);
                    setIsStreaming(false);
                    abortControllerRef.current = null;
                }
                } catch (e) {}
            }

            function read() {
                reader.read().then(function(result) {
                    if (result.done) { setLoading(false); setIsStreaming(false); abortControllerRef.current = null; return; }
                    buffer += decoder.decode(result.value, { stream: true });
                    var lines = buffer.split('\n');
                    buffer = lines.pop();
                    lines.forEach(processLine);
                    read();
                }).catch(function(err) {
                    if (err && err.name === 'AbortError') return;
                    setError('Stream error.');
                    setLoading(false);
                    setIsStreaming(false);
                    abortControllerRef.current = null;
                });
            }

            read();
        }).catch(function(err) {
            if (err && err.name === 'AbortError') return;
            setError('Network error. Please try again.');
            setLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
        });
    };

    // Hint (Socratic tutor)
    var sendHint = function() {
        var attempt = hintText.trim();
        if (!attempt || hintLoading) return;
        setHintLoading(true);
        setHintResponse('');

        safeFetch(API_BASE_URL + '/learnwithai/ai/hint', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            body: JSON.stringify({
                topic: topic,
                currentAttempt: attempt,
                sessionId: sessionId,
            }),
        })
            .then(function(data) {
                setHintResponse(data.success ? data.data.hint : (data.message || 'Could not generate hint.'));
            })
            .catch(function() { setHintResponse('Network error.'); })
            .finally(function() { setHintLoading(false); });
    };

    var handleKeyDown = function(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    function getCaretFromPoint(x, y) {
        if (document.caretPositionFromPoint) {
            var pos = document.caretPositionFromPoint(x, y);
            return pos ? { node: pos.offsetNode, offset: pos.offset } : null;
        }
        if (document.caretRangeFromPoint) {
            var r = document.caretRangeFromPoint(x, y);
            return r ? { node: r.startContainer, offset: r.startOffset } : null;
        }
        return null;
    }

    function updateHandlePositions() {
        var sh = startHandleRef.current;
        var eh = endHandleRef.current;
        var ab = askBtnRef.current;
        var sel = window.getSelection();
        var container = messagesContainerRef.current;
        if (!sh || !eh) return;
        var active = sel && sel.rangeCount && sel.toString().trim() && container && sel.anchorNode && container.contains(sel.anchorNode);
        if (!active) {
            sh.style.display = 'none';
            eh.style.display = 'none';
            if (ab) ab.style.display = 'none';
            return;
        }
        var rects = sel.getRangeAt(0).getClientRects();
        if (!rects.length) { sh.style.display = 'none'; eh.style.display = 'none'; if (ab) ab.style.display = 'none'; return; }
        var first = rects[0];
        var last = rects[rects.length - 1];
        var lineGap = Math.round(first.height * 0.2);
        var STEM = 6;
        sh.style.display = 'block';
        sh.style.top = (first.bottom - lineGap + STEM) + 'px';
        sh.style.left = first.left + 'px';
        eh.style.display = 'block';
        eh.style.top = (last.bottom - lineGap + STEM) + 'px';
        eh.style.left = last.right + 'px';
        if (ab) {
            var isPopupOpen = !!document.querySelector('.lw-hover-popup');
            if (isPopupOpen) {
                ab.style.display = 'none';
            } else {
                ab.style.display = 'block';
                ab.style.top = (last.bottom - lineGap + STEM + 12 + 6) + 'px';
                ab.style.left = ((first.left + last.right) / 2) + 'px';
            }
        }
    }

    var startHandleDrag = function(e) {
        e.preventDefault();
        var sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        var endNode = sel.getRangeAt(0).endContainer;
        var endOffset = sel.getRangeAt(0).endOffset;
        var onMove = function(ev) {
            var caret = getCaretFromPoint(ev.clientX, ev.clientY);
            if (!caret) return;
            try {
                var newRange = document.createRange();
                newRange.setStart(caret.node, caret.offset);
                newRange.setEnd(endNode, endOffset);
                if (newRange.collapsed) { newRange.setStart(endNode, endOffset); newRange.setEnd(caret.node, caret.offset); }
                sel.removeAllRanges();
                sel.addRange(newRange);
            } catch (ex) {}
        };
        var onUp = function() { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    var endHandleDrag = function(e) {
        e.preventDefault();
        var sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        var startNode = sel.getRangeAt(0).startContainer;
        var startOffset = sel.getRangeAt(0).startOffset;
        var onMove = function(ev) {
            var caret = getCaretFromPoint(ev.clientX, ev.clientY);
            if (!caret) return;
            try {
                var newRange = document.createRange();
                newRange.setStart(startNode, startOffset);
                newRange.setEnd(caret.node, caret.offset);
                if (newRange.collapsed) { newRange.setStart(caret.node, caret.offset); newRange.setEnd(startNode, startOffset); }
                sel.removeAllRanges();
                sel.addRange(newRange);
            } catch (ex) {}
        };
        var onUp = function() { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // selectionchange: update handles + Ask button position
    useEffect(() => {
        var rafId;
        var nativeSelecting = false;

        var handleSelectionChange = function() {
            if (nativeSelecting) return;
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateHandlePositions);
        };

        var handleDocMouseDown = function(e) {
            var sh = startHandleRef.current;
            var eh = endHandleRef.current;
            var ab = askBtnRef.current;
            if (e.target === sh || e.target === eh || (ab && e.target === ab)) return;
            nativeSelecting = true;
            if (sh) sh.style.display = 'none';
            if (eh) eh.style.display = 'none';
            if (ab) ab.style.display = 'none';
        };

        var handleDocMouseUp = function(e) {
            if (!nativeSelecting) return;
            nativeSelecting = false;
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateHandlePositions);
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        document.addEventListener('mousedown', handleDocMouseDown);
        document.addEventListener('mouseup', handleDocMouseUp);
        return function() {
            cancelAnimationFrame(rafId);
            document.removeEventListener('selectionchange', handleSelectionChange);
            document.removeEventListener('mousedown', handleDocMouseDown);
            document.removeEventListener('mouseup', handleDocMouseUp);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    var askAboutSelected = function(q) {
        var question = (q || hoverQuestion).trim();
        if (!question || hoverLoading) return;
        setHoverAnswer('');
        setHoverLoading(true);

        safeFetch(API_BASE_URL + '/learnwithai/ai/hover', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token,
            },
            body: JSON.stringify({
                topic: topic,
                selected: { text: hoverText },
                originalContext: { fullStepText: hoverContext || '' },
                userQuestion: question,
                sessionId: sessionId,
            }),
        })
            .then(function(data) {
                setHoverAnswer(data.success ? data.data.explanation : (data.message || 'Could not explain.'));
            })
            .catch(function() { setHoverAnswer('Network error.'); })
            .finally(function() { setHoverLoading(false); });
    };

    // ── Extract surrounding context from the DOM when text is selected ──
    // When a user selects a tiny fragment like "3", we need the full sentence
    // or paragraph so the AI understands what the selection refers to.
    function extractSelectionContext(sel) {
        if (!sel || !sel.rangeCount) return '';
        var range = sel.getRangeAt(0);
        var selectedText = sel.toString().trim();

        // Walk up from the selection to find the closest meaningful text block
        // (a .lw-md-p, .lw-md-li, .lw-md-h2, .lw-bubble, or similar element)
        var node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;

        // Find the nearest block-level parent that contains meaningful text
        var blockSelectors = '.lw-md-p, .lw-md-li, .lw-md-h2, .lw-md-h3, .lw-md-h4, .lw-bubble';
        var blockParent = null;
        var current = node;
        while (current && current !== document.body) {
            // Check if this element matches a known block selector
            try {
                if (current.matches && current.matches(blockSelectors)) {
                    blockParent = current;
                    break;
                }
            } catch (e) {}
            current = current.parentElement;
        }

        if (!blockParent) {
            // Fallback: just get the parent element's text
            blockParent = node;
        }

        var fullBlockText = (blockParent.textContent || '').trim();

        // If the block is too small, try going one level higher
        if (fullBlockText.length < 20 && blockParent.parentElement) {
            var parent = blockParent.parentElement;
            var parentText = (parent.textContent || '').trim();
            if (parentText.length > fullBlockText.length) {
                fullBlockText = parentText;
            }
        }

        // If selection is very short (< 15 chars), also try to extract the full
        // sentence from the block text that contains the selection
        if (selectedText.length < 15 && fullBlockText.length > selectedText.length) {
            // Split into sentences (split on period/exclamation/question + space)
            var sentences = fullBlockText.replace(/([.!?])\s+/g, '$1|||').split('|||');
            var matchingSentence = '';
            for (var i = 0; i < sentences.length; i++) {
                if (sentences[i].indexOf(selectedText) !== -1) {
                    matchingSentence = sentences[i].trim();
                    break;
                }
            }
            // If we found a sentence, use it. Otherwise use the full block.
            if (matchingSentence && matchingSentence.length > selectedText.length) {
                // But also include neighboring sentences for richer context
                var sentenceIdx = sentences.indexOf(matchingSentence);
                var contextSentences = [];
                if (sentenceIdx > 0) contextSentences.push(sentences[sentenceIdx - 1]);
                contextSentences.push(matchingSentence);
                if (sentenceIdx < sentences.length - 1) contextSentences.push(sentences[sentenceIdx + 1]);
                return contextSentences.join(' ').trim();
            }
        }

        // Cap context to avoid sending too much (max ~500 chars)
        if (fullBlockText.length > 500) {
            // Find the position of the selected text within the block
            var pos = fullBlockText.indexOf(selectedText);
            if (pos !== -1) {
                var start = Math.max(0, pos - 200);
                var end = Math.min(fullBlockText.length, pos + selectedText.length + 200);
                fullBlockText = (start > 0 ? '...' : '') + fullBlockText.slice(start, end) + (end < fullBlockText.length ? '...' : '');
            } else {
                fullBlockText = fullBlockText.slice(0, 500) + '...';
            }
        }

        return fullBlockText;
    }

    var handleAskBtnClick = function() {
        var sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        var text = sel.toString().trim();
        if (!text) return;
        var ab = askBtnRef.current;
        if (ab) ab.style.display = 'none';

        // Extract surrounding context from DOM before clearing selection
        var context = extractSelectionContext(sel);

        var rect = sel.getRangeAt(0).getBoundingClientRect();
        setHoverText(text);
        setHoverContext(context);
        setHoverPos({
            top: rect.bottom + window.scrollY + 8,
            left: Math.min(rect.left + rect.width / 2, window.innerWidth - 200),
        });
        setHoverQuestion('');
        setHoverAnswer('');
        setShowHoverPopup(true);
    };

    var showWelcome = messages.length === 0 && !loading;
    var foundModel = MODEL_OPTIONS.find(function(m) { return m.key === selectedModel; });
    var currentModelLabel = (foundModel && foundModel.label) || 'Gemini 2.5 Flash';
    var hasDocPanel = docSessions && docSessions.length > 0 && showDocPanel;
    var hasDocSessions = docSessions && docSessions.length > 0;
    var activeDoc = hasDocSessions ? docSessions[activeDocIdx] : null;
    var currentPage = activeDoc && activeDoc.pages ? activeDoc.pages.find(function(p) { return p.pageNum === activeDoc.currentPage; }) : null;

    return (
        <div className="lw-chat-wrapper">

        <div className="lw-chat">
            {/* Welcome screen */}
            {showWelcome && (
                <div className="lw-welcome">
                    <div className="lw-welcome-icon">✦</div>
                    <h2 className="lw-welcome-title">What do you want to learn?</h2>
                    <p className="lw-welcome-sub">Ask anything about <strong>{topic}</strong></p>
                    <div className="lw-suggestions">
                        {SUGGESTIONS.map(function(s, i) {
                            return (
                                <button
                                    key={i}
                                    className="lw-suggestion-chip"
                                    onClick={function() { sendMessage(s); }}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Messages */}
            {!showWelcome && (
                <div className="lw-messages" ref={messagesContainerRef}>
                    {messages.map(function(msg, i) {
                        return (
                            <div key={i} className={'lw-msg ' + msg.from}>
                                <div className={msg.isAcademicNotice ? 'lw-bubble lw-academic-notice' : 'lw-bubble'}>
                                    {msg.isAcademicNotice ? (
                                        <div className="lw-academic-notice-content">
                                            <span className="lw-academic-notice-icon">&#x1F393;</span>
                                            <span>{msg.text}</span>
                                        </div>
                                    ) : msg.from === 'ai' ? renderAIText(msg.text) : msg.text}
                                </div>
                            </div>
                        );
                    })}

                    {loading && (
                        <div className="lw-msg ai" style={{ alignItems: 'center' }}>
                            <div className="lw-bubble">
                                <div className="lw-typing">
                                    <span /><span /><span />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && <div className="lw-error">{error}</div>}

            {/* Hint Panel */}
            {showHintInput && (
                <div className="lw-hint-panel">
                    <div className="lw-hint-header">
                        <span className="lw-hint-label">💡 Socratic Hint</span>
                        <button className="lw-hover-close" onClick={function() { setShowHintInput(false); setHintResponse(''); }}>×</button>
                    </div>
                    {!hintResponse && (
                        <div className="lw-hint-input-row">
                            <input
                                className="lw-hover-input"
                                placeholder="Describe your current attempt or what you've tried…"
                                value={hintText}
                                onChange={function(e) { setHintText(e.target.value); }}
                                onKeyDown={function(e) { if (e.key === 'Enter') sendHint(); }}
                                disabled={hintLoading}
                            />
                            <button className="lw-hover-send" onClick={sendHint} disabled={!hintText.trim() || hintLoading}>
                                {hintLoading ? '…' : '→'}
                            </button>
                        </div>
                    )}
                    {hintLoading && (
                        <div className="lw-typing" style={{ margin: '8px 0' }}>
                            <span /><span /><span />
                        </div>
                    )}
                    {hintResponse && (
                        <div className="lw-hover-answer">{renderAIText(hintResponse)}</div>
                    )}
                </div>
            )}

            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileSelect} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileSelect} />

            {/* Pill input */}
            <div className="lw-input-area">
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingBottom: docSessions.length > 0 || attachedFile ? '8px' : '0' }}>
                    {docSessions.map(function(doc, idx) {
                        return (
                            <div className="lw-file-chip" key={doc.id || idx} onClick={function() { setActiveDocIdx(idx); setShowDocPanel(true); }} style={{ cursor: 'pointer' }}>
                                <i className={'fi ' + (doc.fileType === 'pdf' ? 'fi-rr-file-pdf' : 'fi-rr-picture')} />
                                <span className="lw-file-chip-name">{doc.fileName}</span>
                                <button className="lw-file-chip-remove" onClick={function(e) { e.stopPropagation(); removeDoc(idx); }}>×</button>
                            </div>
                        );
                    })}
                    {attachedFile && docSessions.length === 0 && (
                        <div className="lw-file-chip">
                            <i className={'fi ' + (attachedFile.type === 'application/pdf' ? 'fi-rr-file-pdf' : 'fi-rr-picture')} />
                            <span className="lw-file-chip-name">{attachedFile.name}</span>
                            <button className="lw-file-chip-remove" onClick={function() { setAttachedFile(null); }}>×</button>
                        </div>
                    )}
                </div>
                <div className="lw-input-wrapper">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder={'Ask about ' + topic + '…'}
                        value={input}
                        onChange={function(e) { setInput(e.target.value); }}
                        onKeyDown={handleKeyDown}
                        disabled={isStreaming}
                    />
                    {/* Hint button */}
                    <button
                        className="lw-hint-btn"
                        onClick={function() { setShowHintInput(function(s) { return !s; }); }}
                        title="Get a hint (Socratic mode)"
                        disabled={isStreaming}
                    >
                        💡
                    </button>
                    {/* Attach button */}
                    <div className="lw-attach-wrap" ref={attachMenuRef}>
                        {showAttachMenu && (
                            <div className="lw-attach-menu">
                                <button className="lw-attach-option" onClick={function() { fileInputRef.current.click(); setShowAttachMenu(false); }}>
                                    <i className="fi fi-rr-upload" />
                                    <span>Upload PDF or Image</span>
                                </button>
                                <button className="lw-attach-option" onClick={function() { cameraInputRef.current.click(); setShowAttachMenu(false); }}>
                                    <i className="fi fi-rr-camera" />
                                    <span>Take Photo</span>
                                </button>
                            </div>
                        )}
                        <button
                            className={'lw-attach-btn' + (attachedFile ? ' lw-attach-btn--active' : '')}
                            onClick={function() { setShowAttachMenu(function(s) { return !s; }); }}
                            title="Attach file"
                            disabled={isStreaming}
                        >
                            <i className="fi fi-sr-paperclip-vertical" />
                        </button>
                    </div>
                    {isStreaming ? (
                        <button
                            className="lw-send-btn lw-stop-btn"
                            onClick={stopGeneration}
                            title="Stop generating"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="4" y="4" width="16" height="16" rx="2" />
                            </svg>
                        </button>
                    ) : (
                        <button
                            className="lw-send-btn"
                            onClick={function() { sendMessage(); }}
                            disabled={!input.trim()}
                            title="Send"
                        >
                            <i className="fi fi-rr-arrow-small-up" style={{ fontSize: '18px', lineHeight: 1 }} />
                        </button>
                    )}
                </div>
                {/* Model + Topic row below input */}
                <div className="lw-below-input-row">
                    <div className="lw-model-label-wrap" ref={modelMenuRef}>
                        <button
                            className="lw-model-label"
                            onClick={function() { setShowModelMenu(function(s) { return !s; }); }}
                        >
                            <span>{currentModelLabel}</span>
                            <i className="fi fi-rr-angle-small-down" />
                        </button>
                        {showModelMenu && (
                            <div className="lw-model-menu">
                                <div className="lw-model-menu-section">
                                    <div className="lw-model-menu-title">Model</div>
                                    {MODEL_OPTIONS.map(function(m) {
                                        return (
                                            <button
                                                key={m.key}
                                                className={'lw-model-menu-item' + (selectedModel === m.key ? ' active' : '')}
                                                onClick={function() { setSelectedModel(m.key); }}
                                            >
                                                <span className="lw-model-menu-name">{m.label}</span>
                                                <span className="lw-model-menu-desc">{m.desc}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="lw-model-menu-section">
                                    <div className="lw-model-menu-title">Response Style</div>
                                    {MODE_OPTIONS.map(function(m) {
                                        return (
                                            <button
                                                key={m.key}
                                                className={'lw-model-menu-item' + (selectedMode === m.key ? ' active' : '')}
                                                onClick={function() { setSelectedMode(m.key); }}
                                            >
                                                <span className="lw-model-menu-name">{m.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Apps menu button */}
                    {activeTab && onTabChange && (
                        <div className="lw-apps-wrap" ref={appsMenuRef}>
                            <button
                                className="lw-apps-btn"
                                onClick={function() { setShowAppsMenu(function(s) { return !s; }); }}
                                title="Switch view"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <rect x="1" y="1" width="6" height="6" rx="1.2" />
                                    <rect x="9" y="1" width="6" height="6" rx="1.2" />
                                    <rect x="1" y="9" width="6" height="6" rx="1.2" />
                                    <rect x="9" y="9" width="6" height="6" rx="1.2" />
                                </svg>
                            </button>
                            {showAppsMenu && (
                                <div className="lw-apps-dropdown">
                                    {TABS.map(function(tab) {
                                        return (
                                            <button
                                                key={tab.key}
                                                className={'lw-apps-dropdown-item' + (activeTab === tab.key ? ' active' : '')}
                                                onClick={function() {
                                                    onTabChange(tab.key);
                                                    setShowAppsMenu(false);
                                                }}
                                            >
                                                <span className="lw-apps-dropdown-icon">{tab.icon}</span>
                                                <span>{tab.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {loadingTopics ? (
                        <select className="lw-topic-select-inline" disabled>
                            <option>Loading…</option>
                        </select>
                    ) : (
                        <select
                            className="lw-topic-select-inline"
                            value={selectedTopic}
                            onChange={function(e) { onTopicChange(e.target.value); }}
                        >
                            {topics.map(function(t) {
                                return (
                                    <option key={t.slug} value={t.slug}>{t.displayName}</option>
                                );
                            })}
                        </select>
                    )}
                </div>
            </div>

            {/* Selection handles + Ask button */}
            <div ref={startHandleRef} className="lw-sel-handle lw-sel-handle-start" style={{ display: 'none' }} onMouseDown={startHandleDrag} />
            <div ref={endHandleRef} className="lw-sel-handle lw-sel-handle-end" style={{ display: 'none' }} onMouseDown={endHandleDrag} />
            <button ref={askBtnRef} className="lw-ask-btn" style={{ display: 'none' }} onClick={handleAskBtnClick}>Ask</button>

            {/* Hover popup */}
            {showHoverPopup && (
                <div
                    className="lw-hover-popup"
                    style={{ top: hoverPos.top, left: hoverPos.left }}
                >
                    <div className="lw-hover-popup-header">
                        <span className="lw-hover-popup-label">
                            "{hoverText.length > 50 ? hoverText.slice(0, 50) + '…' : hoverText}"
                        </span>
                        <button className="lw-hover-close" onClick={function() { setShowHoverPopup(false); setTimeout(updateHandlePositions, 50); }}>×</button>
                    </div>

                    {!hoverAnswer && !hoverLoading && (
                        <>
                            <div className="lw-hover-input-row">
                                <input
                                    ref={hoverInputRef}
                                    className="lw-hover-input"
                                    placeholder="Ask about this…"
                                    value={hoverQuestion}
                                    onChange={function(e) { setHoverQuestion(e.target.value); }}
                                    onKeyDown={function(e) { if (e.key === 'Enter') askAboutSelected(); }}
                                />
                                <button
                                    className="lw-hover-send"
                                    onClick={function() { askAboutSelected(); }}
                                    disabled={!hoverQuestion.trim()}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                                    </svg>
                                </button>
                            </div>
                            <div className="lw-hover-chips">
                                {['Explain this', 'Give an example', 'Why is this important?'].map(function(chip) {
                                    return (
                                        <button key={chip} className="lw-hover-chip" onClick={function() { askAboutSelected(chip); }}>
                                            {chip}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {hoverLoading && (
                        <div className="lw-typing" style={{ margin: '8px 0' }}>
                            <span /><span /><span />
                        </div>
                    )}

                    {hoverAnswer && (
                        <div className="lw-hover-answer">{renderAIText(hoverAnswer)}</div>
                    )}
                </div>
            )}
        </div>

            {/* Document Panel (right side) */}
            {hasDocSessions && (
                <>
                <div
                    className="lw-mobile-resizer"
                    onMouseDown={handleMobileResizeStart}
                    onTouchStart={handleMobileResizeStart}
                >
                    <i className="fi fi-sr-sort"></i>
                </div>
                <div className={'lw-doc-panel' + (hasDocPanel ? ' lw-doc-panel--open' : ' lw-doc-panel--closed')} style={mobileDocHeight ? { height: mobileDocHeight, maxHeight: 'none' } : {}}>
                    <div className="lw-doc-panel-header" style={{ padding: '0', borderBottom: '1px solid #ddd', display: 'flex', overflowX: 'auto', backgroundColor: '#f9f9f9', alignItems: 'center' }}>
                        {docSessions.map(function(doc, idx) {
                            var isActive = idx === activeDocIdx;
                            return (
                                <div
                                    key={idx}
                                    onClick={function() { setActiveDocIdx(idx); }}
                                    style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        backgroundColor: isActive ? '#fff' : 'transparent',
                                        borderBottom: isActive ? '2px solid #007bff' : '2px solid transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        minWidth: '120px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <span style={{ fontWeight: isActive ? '600' : '400', fontSize: '14px', color: isActive ? '#333' : '#666' }}>
                                        {doc.fileName.length > 15 ? doc.fileName.slice(0, 15) + '...' : doc.fileName}
                                    </span>
                                    {doc.status === 'processing' && <span style={{ fontSize: '10px', color: '#888' }}>(🔄)</span>}
                                    {doc.status === 'error' && <span style={{ fontSize: '10px', color: 'red' }}>(❌)</span>}
                                </div>
                            );
                        })}
                        <div style={{ padding: '0 15px', display: 'flex', alignItems: 'center' }}>
                           <button onClick={function() { fileInputRef.current && fileInputRef.current.click(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#007bff', fontSize: '20px' }}>+</button>
                        </div>
                        <div style={{ marginLeft: 'auto', padding: '0 15px', display: 'flex', alignItems: 'center' }}>
                           <button onClick={closeDocPanel} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#666' }} title="Hide document panel">&times;</button>
                        </div>
                    </div>

                    {activeDoc && activeDoc.loading && (
                        <div className="lw-doc-panel-loading">
                            <div className="lw-typing"><span /><span /><span /></div>
                            <p>Extracting text from document... {activeDoc.progress ? activeDoc.progress + '%' : ''}</p>
                        </div>
                    )}

                    {activeDoc && activeDoc.error && (
                        <div className="lw-doc-panel-error">
                            <p>{activeDoc.error}</p>
                            <button onClick={function() { removeDoc(activeDocIdx); }}>Dismiss</button>
                        </div>
                    )}

                    {activeDoc && activeDoc.status === 'ready' && (
                        <>
                            <div className="lw-doc-panel-content" onMouseUp={handleDocTextSelect} style={{ flex: 1, overflowY: 'auto' }}>
                                {currentPage ? renderAIText(currentPage.extractedText) : <p>No content for this page.</p>}
                            </div>
                            <div className="lw-doc-page-nav" style={{ borderTop: '1px solid #ddd', padding: '10px', background: '#fff', display: 'flex', justifyContent: 'center', gap: '15px', alignItems: 'center', opacity: activeDoc.totalPages > 1 ? 1 : 0.4, pointerEvents: activeDoc.totalPages > 1 ? 'auto' : 'none' }}>
                                <button
                                    disabled={activeDoc.currentPage <= 1}
                                    onClick={function() { setDocPage(activeDoc.currentPage - 1); }}
                                    style={{ padding: '6px 12px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#f5f5f5', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                ><i className="fi fi-rr-arrow-small-left"></i></button>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#555' }}>Page {activeDoc.currentPage} of {activeDoc.totalPages}</span>
                                <button
                                    disabled={activeDoc.currentPage >= activeDoc.totalPages}
                                    onClick={function() { setDocPage(activeDoc.currentPage + 1); }}
                                    style={{ padding: '6px 12px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#f5f5f5', color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                ><i className="fi fi-rr-arrow-small-right"></i></button>
                            </div>
                        </>
                    )}

                    {/* All Selections Across Documents */}
                    {docSelections.length > 0 && (
                        <div className="lw-doc-selections-container" style={{ borderTop: '1px solid #eee', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '8px 12px', background: '#f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                                <span>{docSelections.length} Selection{docSelections.length !== 1 ? 's' : ''} (Cross-Doc)</span>
                                <button onClick={function() { setDocSelections([]); }} style={{ background: 'none', border: 'none', color: '#d9534f', cursor: 'pointer', fontSize: '12px' }}>Clear All</button>
                            </div>
                            <div style={{ maxHeight: '150px', overflowY: 'auto', padding: '8px' }}>
                                {docSelections.map(function(sel) {
                                    return (
                                        <div key={sel.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', padding: '6px 8px', marginBottom: '6px', fontSize: '13px' }}>
                                            <div style={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }} onClick={function() {
                                                var targetIdx = docSessions.findIndex(function(d) { return d.fileName === sel.docName; });
                                                if (targetIdx !== -1) {
                                                    setActiveDocIdx(targetIdx);
                                                    setDocSessions(function(prev) {
                                                        var copy = prev.slice();
                                                        copy[targetIdx] = Object.assign({}, copy[targetIdx], { currentPage: sel.pageNum });
                                                        return copy;
                                                    });
                                                    var tries = 0;
                                                     var attemptFind = function() {
                                                         window.getSelection().removeAllRanges();
                                                         var found = window.find(sel.text, false, false, true, false, false, false);
                                                         if (!found && tries < 10) {
                                                             tries++;
                                                             setTimeout(attemptFind, 150);
                                                         }
                                                     };
                                                     setTimeout(attemptFind, 200);
                                                }
                                            }}>
                                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#333' }}>"{sel.text}"</div>
                                                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{sel.docName} (Pg {sel.pageNum})</div>
                                             </div>
                                            <button onClick={function() { setDocSelections(function(prev) { return prev.filter(function(s) { return s.id !== sel.id; }); }); }} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '0 5px', fontSize: '16px' }}>&times;</button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                </>
            )}

            {showPaywall && (
                <div className="lw-paywall-modal-overlay" onClick={() => setShowPaywall(false)}>
                    <div className="lw-paywall-modal-container" onClick={e => e.stopPropagation()}>
                        <button className="lw-paywall-close" onClick={() => setShowPaywall(false)} aria-label="Close">
                            <i className="fi fi-rr-cross-small"></i>
                        </button>
                        <div className="lw-paywall-content">
                            <div className="lw-paywall-icon-wrap">
                                <i className="fi fi-sr-gem"></i>
                            </div>
                            <h3 className="lw-paywall-title">Free Limit Reached</h3>
                            <p className="lw-paywall-desc">You've used your 2 free AI prompts. Subscribe to get unlimited access to Learn with AI and all premium apps!</p>
                            <a href="/subscription" className="lw-paywall-btn">
                                View Plans
                            </a>
                            <p className="lw-paywall-footer">Secured by Razorpay. Cancel anytime.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChatView;
